// ============================================================================
// DAY VIEW — arriving / staying over / departing on one date
// ============================================================================
// The second tab of the Booking Worksheet. Three collapsible sections rather
// than sub-tabs: the agent wants the whole day's picture at once, and sub-tabs
// would hide two-thirds of it behind another click.
//
// Rows are ordinary Booking objects rendered through the SAME SyutGrid columns
// and the SAME row-action menu as the All Bookings tab. That is what makes this
// cheap — only the query is new. Nothing here re-implements a row.
//
// Design: crs-doc/CRS-Booking-Worksheet-Day-View-Design.md §6.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem } from 'components/ui';
import { bookingService } from 'api/services/bookingService';
import type { Booking, DayGuestListResponse } from 'api/types/booking.types';
import { parseDayKey, todayKey, isValidDayKey } from 'utils/calendarDate';

type SectionKey = 'arriving' | 'stayingOver' | 'departing';

/**
 * Section labels follow the focus date, because "in-house" is a front-office
 * STATUS and for a future date nobody has checked in yet. Calling a projection
 * "in-house" is the one thing guaranteed to mislead.
 */
const sectionMeta = (
  key: SectionKey,
  relation: 'past' | 'today' | 'future',
): { title: string; hint: string; empty: string } => {
  if (key === 'arriving') {
    return relation === 'past'
      ? { title: 'Arrived', hint: 'actuals', empty: 'Nobody arrived on this date.' }
      : relation === 'today'
        ? { title: 'Expected arrivals', hint: 'Primary action · Check In', empty: 'No arrivals today.' }
        : { title: 'Arriving', hint: 'projected from bookings', empty: 'No arrivals on this date.' };
  }
  if (key === 'departing') {
    return relation === 'past'
      ? { title: 'Departed', hint: 'actuals', empty: 'Nobody departed on this date.' }
      : relation === 'today'
        ? { title: 'Due out', hint: 'Primary action · Check Out', empty: 'No departures today.' }
        : { title: 'Departing', hint: 'projected from bookings', empty: 'No departures on this date.' };
  }
  return relation === 'past'
    ? { title: 'Stayed', hint: 'actuals', empty: 'Nobody stayed over on this date.' }
    : relation === 'today'
      ? { title: 'In-house', hint: 'Primary action · View', empty: 'No guests in-house.' }
      : { title: 'Staying over', hint: 'projected from bookings', empty: 'Nobody staying over on this date.' };
};

interface DayViewPanelProps {
  propertyId: string;
  /** yyyy-MM-dd. */
  date: string;
  /** The worksheet's column set — reused verbatim. */
  columns: SyutGridColumn<Booking>[];
  /** The worksheet's row-action menu — reused verbatim. */
  rowActions: (row: Booking) => ActionMenuItem[];
  /**
   * Day-scoped search term. Held by the worksheet so the header search box and
   * this panel cannot disagree about what is being searched.
   */
  searchTerm: string;
  /** Escape hatch — search all bookings instead of just this day. */
  onEscapeSearch: () => void;
  /** Switches to All Bookings with this date applied as a visible chip. */
  onOpenFullList: () => void;
  /** Bumped by the worksheet after any mutation, to force a reload. */
  refreshKey: number;
}

const DayViewPanel: React.FC<DayViewPanelProps> = ({
  propertyId, date, columns, rowActions,
  searchTerm, onEscapeSearch, onOpenFullList, refreshKey,
}) => {
  const [data, setData] = useState<DayGuestListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>({
    arriving: false, stayingOver: false, departing: false,
  });

  const load = useCallback(async () => {
    if (!propertyId || !date) { setData(null); return; }
    setLoading(true);
    setError('');
    try {
      const res = await bookingService.getDayGuestList(propertyId, date, searchTerm);
      setData(res.data ?? null);
    } catch {
      setData(null);
      setError('Could not load the guest list for this date.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, date, searchTerm]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const relation: 'past' | 'today' | 'future' = useMemo(() => {
    const t = todayKey();
    return date === t ? 'today' : date < t ? 'past' : 'future';
  }, [date]);

  const dateLabel = useMemo(
    () => (isValidDayKey(date)
      ? parseDayKey(date).toLocaleDateString('en-IN',
          { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
      : '—'),
    [date],
  );

  const totalShown = data
    ? data.arriving.items.length + data.stayingOver.items.length + data.departing.items.length
    : 0;
  const totalAll = data
    ? data.arriving.total + data.stayingOver.total + data.departing.total
    : 0;

  if (!propertyId) {
    return (
      <div style={{ padding: '1.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Select a property in the filter panel to see the day's guest list.
      </div>
    );
  }

  const renderSection = (key: SectionKey) => {
    const group = data?.[key];
    const meta = sectionMeta(key, relation);
    const isCollapsed = collapsed[key];
    const items = group?.items ?? [];

    return (
      <div key={key} style={{ marginBottom: '0.75rem' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setCollapsed((c) => ({ ...c, [key]: !c[key] }));
            }
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.45rem 0.7rem', cursor: 'pointer', userSelect: 'none',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: isCollapsed ? '6px' : '6px 6px 0 0',
            fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)',
          }}
        >
          {isCollapsed ? <IconSystem.chevronRight size={13} /> : <IconSystem.chevronDown size={13} />}
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.03em' }}>{meta.title}</span>
          <span style={{ color: 'var(--primary)' }}>({group?.total ?? 0})</span>
          <span style={{
            marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 400, color: 'var(--text-muted)',
          }}>
            {meta.hint}
          </span>
        </div>

        {!isCollapsed && (
          items.length === 0 ? (
            <div style={{
              padding: '0.9rem 0.7rem', fontSize: '0.78rem', color: 'var(--text-muted)',
              border: '1px solid var(--border-color)', borderTop: 'none',
              borderRadius: '0 0 6px 6px',
            }}>
              {loading ? 'Loading…' : meta.empty}
            </div>
          ) : (
            <SyutGrid
              data={items}
              columns={columns}
              enableSorting
              rowActions={rowActions}
              emptyMessage={meta.empty}
              /* The row-action menu stays pinned to the right edge — these
                 sections carry the worksheet's full column set, so it would
                 otherwise sit off-screen and need a horizontal scroll to reach.
                 Booking # is pinned left for the same reason in reverse: once
                 you have scrolled right to the actions, an unidentifiable row
                 makes a frozen action button worse than no frozen column at
                 all. Matches the All Bookings grid. */
              frozenLeftColumns={['bookingNumber']}
              frozenRightColumns={['actions']}
            />
          )
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '0.5rem 0.75rem 1rem', overflowY: 'auto', height: '100%' }}>
      {/* Header: the date, the unsold position, and the way out to the full list. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
        paddingBottom: '0.5rem', borderBottom: '1px solid var(--border-color)',
        marginBottom: '0.6rem',
      }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 800 }}>{dateLabel}</span>
        {relation === 'future' && (
          <span style={{
            fontSize: '0.62rem', fontWeight: 700, padding: '0.1rem 0.45rem',
            borderRadius: '999px', border: '1px solid var(--border-color)',
            color: 'var(--text-muted)',
          }}>
            PROJECTED FROM BOOKINGS
          </span>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {totalAll} booking{totalAll !== 1 ? 's' : ''} touching this date
        </span>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          className="btn btn-secondary"
          style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }}
          onClick={onOpenFullList}
          title="Switch to All Bookings, filtered to this date"
        >
          Open full list ↗
        </button>
      </div>

      {/* Search scope. Stated, not implied — and the escape is always visible,
          not only on zero results. Without it an agent searches a guest, sees
          nothing, and concludes the booking does not exist when it is simply on
          another date. */}
      {searchTerm && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
          Searching <b>{totalShown}</b> result{totalShown !== 1 ? 's' : ''} on{' '}
          {parseDayKey(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          {' · '}
          <button
            type="button"
            onClick={onEscapeSearch}
            style={{
              border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
              color: 'var(--primary)', fontWeight: 700, fontSize: '0.72rem', fontFamily: 'inherit',
            }}
          >
            Search all bookings instead ↗
          </button>
        </div>
      )}

      {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}

      {data?.capped && (
        <div style={{
          fontSize: '0.72rem', color: 'var(--warning, #d97706)', marginBottom: '0.5rem',
          border: '1px solid var(--warning, #d97706)', borderRadius: '6px',
          padding: '0.35rem 0.6rem',
        }}>
          Showing the first {data.cap} of {data.totalCount} bookings — section counts above are
          complete. Use <b>Open full list</b> for everything.
        </div>
      )}

      {renderSection('arriving')}
      {renderSection('stayingOver')}
      {renderSection('departing')}
    </div>
  );
};

export default DayViewPanel;
